import React, { Fragment, useEffect, useRef } from "react";
import { useEffectOnce } from "../../hooks/useeffectonce";
import { useSelector, useDispatch } from "react-redux";
import {
  setProcessing,
  setPageValue,
  loadPage,
  setPanes,
  loadDataset,
  createEntity,
  setFormServerErrors,
  entityCloned,
  loadEntity,
  removeEntityFromUI,
  saveEntity,
  deleteEntity,
  setFormValue,
  setFieldValue,
  disposePage,
} from "../../redux/slices/alertspageslice";
import {
  setCurentPage,
  setCurrentPageIsDirty,
  setCurrentPage,
} from "../../redux/slices/currentpageslice";
import { addNotification } from "../../redux/slices/notificationsslice";
import {
  cloneAlert,
  initEntityCloner,
} from "../../redux/slices/entityclonerslice";
import { notificationDurationMiddle, deepCompare, updateRuleAtPath } from "../../lib/utils";
import { hideAppDialog, setAppDialog } from "../../redux/slices/appdialogslice";
import { useNavigate, useLocation } from "react-router-dom";
import { apiErrorsHandler, getErrorMessages } from "../../api/httpclient";
import { Page } from "../page";
import { EntityCloner } from "../../components/entitycloner";
import { AlertsPageBody } from "./alertspagebody";
import { formValidator } from "./alertspageformvalidator";
import { BLOCK_DIRTY_MESSAGE } from "../../settings";

import "./alertspage.css";
import { alertsMaxReached } from "../../routing/authorizationhandlers";
import i18next from "i18next";
import { useTranslation } from "react-i18next";

export const AlertsPage = (props) => {
  const dispatch = useDispatch();
  const currentTheme = useSelector((state) => state.currentTheme);
  const pageState = useSelector((state) => state.alertsPage);
  const currentPageState = useSelector((state) => state.currentPage);
  const currentUser = useSelector((state) => state.currentUser);
  const navigate = useNavigate();

  const { t } = useTranslation();

  let currentPageId = "alertsPage";

  /*block navigation if dirty*/
  const updateDirtyState = () => {
    //we need to exclude some fields from the state comparison; if the user opens or closes a section, this shall NOT affect isDirty
    let {
      generalOpen,
      miscOpen,
      expressionOpen,
      groupByOpen,
      filterOpen,
      ...fiv
    } = pageState.formInitialValues;
    let {
      generalOpen: _generalOpen,
      miscOpen: _miscOpen,
      expressionOpen: _expressionOpen,
      groupByOpen: _groupByOpen,
      filterOpen: _filterOpen,
      ...fv
    } = pageState.formValues;

    let isDirty = !deepCompare(fiv, fv);
    let wasDirty = currentPageState.isDirty;

    dispatch(setCurrentPageIsDirty(isDirty));
  };

  const blockNavigationIfDirty = (msg) => {
    if (currentPageState.isDirty) {
      if (window.confirm(msg ? msg : BLOCK_DIRTY_MESSAGE) == true) {
        dispatch(setCurrentPageIsDirty(false));
        return false;
      } else return true;
    } else return false;
  };
  /*block navigation if dirty*/

  /*initial page load*/
  useEffectOnce(() => {
    dispatch(loadPage(t)).then(
      (response) => {
        dispatch(setCurrentPage({ id: currentPageId, isDirty: false }));
      },
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          //notify with error
          if (!errorMessages || errorMessages.length == 0)
            dispatch(
              addNotification({
                text: t("alerts.loading_errormessage"),
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              }),
            );
          else {
            errorMessages = getErrorMessages(errorMessages, 1);
            dispatch(
              addNotification({
                text: errorMessages[0],
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              }),
            );
          }

          //go home
          navigate("/", { replace: true });
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          dispatch(
            addNotification({
              text: t("alerts.unknown_error"),
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            }),
          );
          navigate("/", { replace: true });
        };

        apiErrorsHandler({}, dispatch, navigate, err, "/", {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      },
    );

    return () => {
      dispatch(setCurrentPage({ id: "", isDirty: false }));
      dispatch(disposePage(true));
    };
  }, []);
  /*initial load*/

  /*splitter*/
  const handleOnSplitterChange = (e) => {
    dispatch(setPanes(e.newState));
  };
  /*splitter*/

  /*panel bar*/
  const dataSetsPanelRef = useRef(null);

  //on data set change
  const handleOnDatasetChange = (e) => {
    const selectedItemId = e.value;

    if (blockNavigationIfDirty() == true) return;

    dispatch(loadDataset(selectedItemId, t)).then(
      (response) => {},
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          if (!errorMessages || errorMessages.length == 0)
            errorMessages = [t("alerts.unknown_error")];
          else errorMessages = getErrorMessages(errorMessages, 1);

          dispatch(
            addNotification({
              text: errorMessages[0],
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            }),
          );
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          let errorMessage = t("alerts.unknown_error");
          dispatch(
            addNotification({
              text: errorMessage,
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            }),
          );
        };

        apiErrorsHandler({}, dispatch, navigate, err, null, {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      },
    );
  };

  /*panel bar*/

  /*on user checked/unchecked*/
  const handleOnUserCheck = (item) => {
    let _viewers = pageState.formValues.Viewers.map((e) => {
      if (e.Id == item.Id) return { ...e, Checked: !e.Checked };
      else return { ...e };
    });

    dispatch(setFormValue({ id: "Viewers", value: _viewers }));
  };

  const handleOnUsersCheck = (item) => {
    let items = item.value;

    let viewers = pageState.formValues.Viewers.map((e) => {
      if (items && items.length > 0 && items.find((i) => i.Id == e.Id))
        return { ...e, Checked: true };
      else return { ...e, Checked: false };
    });

    dispatch(setFormValue({ id: "Viewers", value: viewers }));
  };
  /*on user checked/unchecked*/

  /*on group checked/unchecked*/
  const handleOnGroupCheck = (item) => {
    let _groups = pageState.formValues.Groups.map((e) => {
      if (e.Id == item.Id) return { ...e, Checked: !e.Checked };
      else return { ...e };
    });

    dispatch(setFormValue({ id: "Groups", value: _groups }));
  };

  const handleOnGroupsCheck = (item) => {
    let items = item.value;

    let groups = pageState.formValues.Groups.map((e) => {
      if (items && items.length > 0 && items.find((i) => i.Id == e.Id))
        return { ...e, Checked: true };
      else return { ...e, Checked: false };
    });

    dispatch(setFormValue({ id: "Groups", value: groups }));
  };
  /*on group checked/unchecked*/

  /*on groupedfield checked/unchecked*/
  const handleOnGroupedFieldCheck = (item) => {
    let fields = pageState.formValues.GroupedFields.map((e) => {
      if (e.Id == item.Id) return { ...e, Checked: !e.Checked };
      else return { ...e };
    });

    dispatch(setFormValue({ id: "GroupedFields", value: fields }));
  };

  const handleOnGroupedFieldsCheck = (e) => {
    let items = e.value;

    let fields = pageState.formValues.GroupedFields.map((e) => {
      if (items && items.length > 0 && items.find((i) => i.Id == e.Id))
        return { ...e, Checked: true };
      else return { ...e, Checked: false };
    });

    dispatch(setFormValue({ id: "GroupedFields", value: fields }));
  };
  /*on groupedfield checked/unchecked*/

  /*toolbar*/
  const formValuesRef = useRef(null);
  useEffect(() => {
    formValuesRef.current = pageState.formValues;
  }, [pageState.formValues]);

  const handleOnNew = (e) => {
    if (!currentUser.IsAuthenticated || alertsMaxReached(currentUser)) {
      dispatch(
        addNotification({
          text: t("alerts.authorization_error"),
          type: "error",
          closable: true,
          duration: notificationDurationMiddle,
          position: "topRight",
        }),
      );
      return;
    }

    if (blockNavigationIfDirty() == true) return;

    dispatch(createEntity());

    //nneed to updatw the formInitialValues.WhereObject which starts as "" to become the default object given bu query builder
    window.setTimeout(() => {
      dispatch(
        setPageValue({
          id: "formInitialValues",
          value: { ...formValuesRef.current },
        }),
      );
    }, 2000);
  };

  const handleOnSave = (e) => {
    if (pageState.mode == 1) {
      //create
      if (!currentUser.IsAuthenticated || alertsMaxReached(currentUser)) {
        dispatch(
          addNotification({
            text: t("alerts.authorization_error"),
            type: "error",
            closable: true,
            duration: notificationDurationMiddle,
            position: "topRight",
          }),
        );
        return;
      }
    }

    //make sure no client errors; if so return
    let formErrors = formValidator(pageState.formValues);
    if (formErrors !== undefined) {
      dispatch(
        addNotification({
          text: t("alerts.invalid_info"),
          type: "error",
          closable: true,
          duration: notificationDurationMiddle,
          position: "topRight",
        }),
      );
      return;
    }

    //no client errors => proceed
    dispatch(saveEntity(t)).then(
      (response) => {
        dispatch(setCurrentPageIsDirty(false));
      },
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          if (!errorMessages || errorMessages.length == 0)
            errorMessages = [t("alerts.unknown_error")];
          else {
            errorMessages = getErrorMessages(errorMessages, 1);

            //if the error is entity does not exist => it has been removed => remove it from currentDataset and from editing
            if (payload.data.ErrorCodes.EntityDoesNotExist) {
              dispatch(removeEntityFromUI(pageState.formValues.Id));
              dispatch(
                addNotification({
                  text: errorMessages[0],
                  type: "error",
                  closable: true,
                  duration: notificationDurationMiddle,
                  position: "topRight",
                }),
              );
            } else dispatch(setFormServerErrors(errorMessages));
          }
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          let errorMessage = t("alerts.unknown_error");
          dispatch(setFormServerErrors([errorMessage]));
        };

        apiErrorsHandler({}, dispatch, navigate, err, null, {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      },
    );
  };

  const handleOnClone = (e) => {
    dispatch(
      initEntityCloner({ title: pageState.formValues.Title, navigate: true }),
    );

    dispatch(
      setAppDialog({
        isVisible: true,
        closable: true,
        title: t("alerts.clonealert"),
        className: "",
        content: <EntityCloner className="" />,
        actions: {
          yesButton: {
            title: t("alerts.clone"),
            handler: (e) => {
              dispatch(cloneAlert(t)).then(
                (response) => {
                  dispatch(hideAppDialog());

                  dispatch(
                    addNotification({
                      text: t("alerts.clonesuccess"),
                      type: "success",
                      closable: true,
                      duration: notificationDurationMiddle,
                      position: "topRight",
                    }),
                  );

                  dispatch(
                    entityCloned({
                      id: response.id,
                      title: response.title,
                      navigate: response.navigate,
                    }),
                  );
                },
                (err) => {
                  if (err == "invalidData") {
                    //show notification that title is required
                    dispatch(
                      addNotification({
                        text: t("alerts.clonetitle"),
                        type: "error",
                        closable: true,
                        duration: notificationDurationMiddle,
                        position: "topRight",
                      }),
                    );
                    return;
                  } else if (err == "ignore") {
                    //in this case the user clicked clone while it's cloning
                    //just do nothing
                    return;
                  } else {
                    const handleResponseReceived = (
                      state,
                      dispatch,
                      navigate,
                      payload,
                    ) => {
                      let errorMessages = payload?.data?.ErrorMessages;
                      let errorMessage = t("alerts.unknown_error");

                      if (errorMessages || errorMessages.length > 0) {
                        errorMessages = getErrorMessages(errorMessages, 1);

                        //if the error is entity does not exist => it has been removed => remove it from currentDataset and from editing
                        if (payload.data.ErrorCodes.TitleExists)
                          errorMessage = errorMessages[0];
                      }

                      dispatch(
                        addNotification({
                          text: errorMessage,
                          type: "error",
                          closable: true,
                          duration: notificationDurationMiddle,
                          position: "topRight",
                        }),
                      );
                    };

                    const handleUnhandled = (
                      state,
                      dispatch,
                      navigate,
                      payload,
                    ) => {
                      dispatch(
                        addNotification({
                          text: t("alerts.unknown_error"),
                          type: "error",
                          closable: true,
                          duration: notificationDurationMiddle,
                          position: "topRight",
                        }),
                      );
                    };

                    apiErrorsHandler({}, dispatch, null, err, null, {
                      responseReceived: handleResponseReceived,
                      unhandled: handleUnhandled,
                    });

                    return Promise.resolve();
                  }
                },
              );
            },
          },
          noButton: {
            title: t("alerts.cancel"),
          },
        },
      }),
    );
  };

  const handleOnErrorbarClose = (e) => {
    dispatch(setFormServerErrors([]));
  };

  /*toolbar*/

  /*delete entity*/
  const handleOnDelete = (id) => {
    //id is zero if deleted from toolbar and > 0 if deleted from entities list
    //if id is zero and mode !=2 => miust not happen
    if (!id && pageState.mode != 2) return;

    let isCurrentEntity = false;

    if (!id) //deleted from the toolbar => it's the current entity and mode is 2
    {
      id = pageState.formValues.Id;
      isCurrentEntity = true;
    } else if (pageState.formValues?.Id && pageState.formValues.Id == id)
      isCurrentEntity = true;

    if (isCurrentEntity) {
      //if contains dirty => block
      if (blockNavigationIfDirty() == true) return;
    }

    dispatch(
      setAppDialog({
        isVisible: true,
        className: "alerts-page-are-you-sure",
        title: t("alerts.warning"),
        content: t("alerts.areyousure"),
        actions: {
          yesButton: {
            title: t("alerts.yes"),
            handler: () => {
              dispatch(hideAppDialog());

              dispatch(deleteEntity(id, t)).then(
                (response) => {},
                (err) => {
                  dispatch(setProcessing({ processing: false }));

                  const handleResponseReceived = (
                    state,
                    dispatch,
                    navigate,
                    payload,
                  ) => {
                    let errorMessages = payload?.data?.ErrorMessages;

                    if (!errorMessages || errorMessages.length == 0)
                      errorMessages = [t("alerts.unknown_error")];
                    else {
                      errorMessages = getErrorMessages(errorMessages, 1);

                      if (payload.data.ErrorCodes.EntityDoesNotExist)
                        dispatch(removeEntityFromUI(id));
                    }

                    dispatch(
                      addNotification({
                        text: errorMessages[0],
                        type: "error",
                        closable: true,
                        duration: notificationDurationMiddle,
                        position: "topRight",
                      }),
                    );
                  };

                  const handleUnhandled = (
                    state,
                    dispatch,
                    navigate,
                    payload,
                  ) => {
                    let errorMessage = t("alerts.unknown_error");
                    dispatch(
                      addNotification({
                        text: errorMessage,
                        type: "error",
                        closable: true,
                        duration: notificationDurationMiddle,
                        position: "topRight",
                      }),
                    );
                  };

                  apiErrorsHandler({}, dispatch, navigate, err, null, {
                    responseReceived: handleResponseReceived,
                    unhandled: handleUnhandled,
                  });
                },
              );
            },
          },
          noButton: { title: t("alerts.no") },
        },
      }),
    );
  };
  /*delete entity*/

  /*view/edit entity*/
  const handleOnEntitySelected = (entityId, item) => {
    if (blockNavigationIfDirty() == true) return;

    dispatch(loadEntity(entityId, t)).then(
      (response) => {},
      (err) => {
        //hid eloader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          if (!errorMessages || errorMessages.length == 0)
            errorMessages = [t("alerts.unknown_error")];
          else {
            errorMessages = getErrorMessages(errorMessages, 1);

            if (payload.data.ErrorCodes.EntityDoesNotExist)
              dispatch(removeEntityFromUI(entityId));
          }

          dispatch(
            addNotification({
              text: errorMessages[0],
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            }),
          );
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          let errorMessage = t("alerts.unknown_error");
          dispatch(
            addNotification({
              text: errorMessage,
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            }),
          );
        };

        apiErrorsHandler({}, dispatch, navigate, err, null, {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      },
    );
  };
  /*view/edit entity*/
  //tools visible/hidden
  const handleOnToolsToggle = (e) => {
    const isVisible = pageState.toolsVisible;

    if (isVisible)
      //hide
      dispatch(
        setPanes([
          {
            size: "0%",
            min: "%",
            max: "%",
            collapsible: false,
          },
          {
            min: "100%",
            max: "100%",
            collapsible: false,
          },
        ]),
      );
    else
      dispatch(
        setPanes([
          {
            size: "22%",
            min: "20%",
            max: "25%",
            collapsible: false,
          },
          {
            min: "75%",
            max: "80%",
            collapsible: false,
          },
        ]),
      );

    dispatch(setPageValue({ id: "toolsVisible", value: !isVisible }));
    //dispatch(redrawTiles());
  };

  const handleKeyChange = (keyId, path) => {
    const currentQuery = pageState.formValues.WhereObject;
    const updatedQuery = updateRuleAtPath(currentQuery, path, keyId);
    dispatch(setFormValue({ id: "WhereObject", value: updatedQuery }));
  };

  return (
    <Fragment>
      <Page
        id="alertsPage"
        className="alerts-page"
        title={t("alerts.alerts")}
        animation={props.animation}
      >
        <AlertsPageBody
          onSplitterChange={handleOnSplitterChange}
          onToolsToggle={handleOnToolsToggle}
          dataSetsPanelRef={dataSetsPanelRef}
          onDatasetChange={handleOnDatasetChange}
          onNew={handleOnNew}
          onSave={handleOnSave}
          onClone={handleOnClone}
          onDelete={handleOnDelete}
          onErrorbarClose={handleOnErrorbarClose}
          onEntitySelected={handleOnEntitySelected}
          onEntityDoubleClick={handleOnEntitySelected}
          onUserCheck={handleOnUserCheck}
          onUsersCheck={handleOnUsersCheck}
          onGroupCheck={handleOnGroupCheck}
          onGroupsCheck={handleOnGroupsCheck}
          onGroupedFieldCheck={handleOnGroupedFieldCheck}
          onGroupedFieldsCheck={handleOnGroupedFieldsCheck}
          updateDirtyState={updateDirtyState}
          onKeyChange={handleKeyChange}
        />
      </Page>
      <style>
        {`
                    /*page class*/
                    .alerts-page
                    {
                        background-color: transparent;
                        padding: ${currentTheme.pagePadding};
                    }

                    .alerts-page .k-splitbar.k-splitbar-horizontal
                    {
                        background-color: ${currentTheme.paneSeparatorBg};
                    }

                    .alerts-page .k-splitbar
                    {
                        width: ${currentTheme.paneSeparatorWidth};
                    }

                    /*are you sure to delete*/
                    .alerts-page-are-you-sure .k-widget
                    {
                      /*width of the dialog*/
                      width: 300px;
                    }    
                    
                    .alerts-page-are-you-sure .k-widget .k-dialog-titlebar.k-header.k-header
                    {
                        /*bg color of the header*/
                        background-color: ${currentTheme.primary};
                    }
                    `}
      </style>
    </Fragment>
  );
};

//export const AlertsPage = {Component: Alerts};
